import React from 'react';
import { Compass, FileBarChart, Pin, Wand2 } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { COMPASS_EXAMPLES } from './examples';

interface ResultEmptyStateProps {
    onAsk: (question: string) => void;
    /** Kompakt liste (samtalefanen på nettbrett) i stedet for kort */
    compact?: boolean;
}

/** Eksempelspørsmål som knapper — brukes på startsiden (kort) og i samtalen på smal skjerm (liste). */
export const ExampleList: React.FC<ResultEmptyStateProps> = ({ onAsk, compact }) => {
    const { t } = useTranslation();
    return (
        <div className={compact ? 'flex flex-col gap-2' : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'}>
            {COMPASS_EXAMPLES.map(ex => {
                const Icon = ex.icon;
                const prompt = t(`manager.modules.claudeQuery.workspace.examples.${ex.key}.prompt`, {}, ex.promptDefault);
                return (
                    <button
                        key={ex.key}
                        onClick={() => onAsk(prompt)}
                        className="text-left flex items-start gap-3 min-h-[44px] p-3 rounded-lg border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                            <Icon size={18} />
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-medium text-gray-800">
                                {t(`manager.modules.claudeQuery.workspace.examples.${ex.key}.title`, {}, ex.titleDefault)}
                            </span>
                            <span className="block text-xs text-gray-500">
                                {t(`manager.modules.claudeQuery.workspace.examples.${ex.key}.description`, {}, ex.descriptionDefault)}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

/**
 * Startsiden i resultatkolonnen: forteller hva Compass kan (visninger, justering, lagring) og gir
 * eksempler som viser bredden. Et klikk stiller spørsmålet.
 */
export const ResultEmptyState: React.FC<ResultEmptyStateProps> = ({ onAsk }) => {
    const { t } = useTranslation();
    const features = [
        { icon: Wand2, text: t('manager.modules.claudeQuery.workspace.featureAdjust', {}, 'Juster visningen med egne ord, for eksempel «vis per uke».') },
        { icon: FileBarChart, text: t('manager.modules.claudeQuery.workspace.featureSave', {}, 'Lagre som rapport — dataene hentes på nytt hver gang, uten ny bruk av Compass.') },
        { icon: Pin, text: t('manager.modules.claudeQuery.workspace.featurePin', {}, 'Pin til dashboardet og følg tallene der.') },
    ];
    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Compass size={20} className="text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">
                    {t('manager.modules.claudeQuery.workspace.emptyTitle', {}, 'Resultatet vises her')}
                </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
                {t('manager.modules.claudeQuery.workspace.emptyText', {}, 'Still et spørsmål om lageret. Compass henter dataene og setter sammen en oversikt med nøkkeltall, grafer og tabeller.')}
            </p>
            <ul className="space-y-1.5 mb-6">
                {features.map(({ icon: Icon, text }, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <Icon size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{text}</span>
                    </li>
                ))}
            </ul>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                {t('manager.modules.claudeQuery.workspace.examplesTitle', {}, 'Prøv for eksempel')}
            </h3>
            <ExampleList onAsk={onAsk} />
        </div>
    );
};
